import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import api from "../../services/api";
import Constants from "../../services/constants";
import { exportTableToCsv } from "../../utils/exportTableCsv";

const platformOptions = [
    { label: "iOS", value: "ios" },
    { label: "Android", value: "android" },
    { label: "Web", value: "web" },
];
const sourceOptions = [
    { label: "Subscription", value: "subscription" },
    { label: "Product / Shop", value: "product" },
    { label: "Masked Call", value: "masked_call" },
];

const dateValue = (value) => (value ? new Date(`${value}T00:00:00`) : null);
const dateParam = (value) => (value ? value.toISOString().slice(0, 10) : "");
const formatDate = (value) => value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";
const titleCase = (value) => String(value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function PaymentTable({ heading, allPayments, loading, history, filters, setFilters, pagination }) {
    const [exporting, setExporting] = useState(false);

    // One-off fetch bypassing the redux action on purpose: dispatching
    // getPayments here would overwrite the on-screen paginated slice. This
    // fetches everything matching the current filters for export only.
    const exportAll = async () => {
        setExporting(true);
        try {
            const query = Object.fromEntries(
                Object.entries({ ...filters, page: 1, limit: 10000 }).filter(([, value]) => value !== "")
            );
            const res = await api("get", Constants.END_POINT.PAYMENT_HISTORY, {}, query);
            const rows = res?.data || [];
            exportTableToCsv("payment-history.csv", rows, [
                { header: "Customer", value: (row) => (row.user ? `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim() || row.user.email : "Guest / unavailable") },
                { header: "Amount", value: (row) => `${row.currency} ${Number(row.amount).toFixed(2)}` },
                { header: "Platform", value: (row) => titleCase(row.platform) },
                { header: "Source", value: (row) => titleCase(row.source) },
                { header: "Gateway", value: (row) => titleCase(row.provider) },
                { header: "Payment ID", value: "providerTransactionId" },
                { header: "Paid on", value: (row) => formatDate(row.paidAt) },
                { header: "Status", value: (row) => titleCase(row.status) },
            ]);
        } finally {
            setExporting(false);
        }
    };

    const actions = (payment) => {
        const userId = payment?.user?._id;
        return (
            <Button
                type="button"
                icon="pi pi-eye"
                className="p-button-text p-button-rounded p-button-secondary"
                aria-label="View user"
                title={userId ? "View user" : "User details unavailable"}
                disabled={!userId}
                onClick={() => userId && history(`/user/view/${userId}`)}
            />
        );
    };

    return (
        <div className="col-12">
            <div className="card">
                <div className="flex justify-content-between flex-wrap gap-2 mb-4">
                    <h5>{heading}</h5>
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left"><i className="pi pi-search" /><InputText value={filters.search} onChange={(e) => setFilters({ search: e.target.value })} placeholder="Search name or payment ID" /></span>
                        <Dropdown value={filters.platform} options={platformOptions} onChange={(e) => setFilters({ platform: e.value || "" })} placeholder="Platform" showClear />
                        <Dropdown value={filters.source} options={sourceOptions} onChange={(e) => setFilters({ source: e.value || "" })} placeholder="Payment source" showClear />
                        <Calendar value={dateValue(filters.from)} onChange={(e) => setFilters({ from: dateParam(e.value) })} placeholder="From date" dateFormat="dd/mm/yy" showIcon />
                        <Calendar value={dateValue(filters.to)} onChange={(e) => setFilters({ to: dateParam(e.value) })} placeholder="To date" dateFormat="dd/mm/yy" showIcon />
                        <Button
                            label="Export CSV"
                            icon="pi pi-download"
                            onClick={exportAll}
                            loading={exporting}
                            disabled={!pagination.total}
                            severity="success"
                        />
                    </div>
                </div>
                <DataTable
                    value={allPayments}
                    lazy
                    paginator
                    first={(pagination.page - 1) * pagination.limit}
                    rows={pagination.limit}
                    totalRecords={pagination.total}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    onPage={(event) => setFilters({ page: event.page + 1, limit: event.rows })}
                    loading={loading}
                    emptyMessage="No successful payments found."
                    className="custom-datatable-header"
                    showGridlines
                >
                    <Column header="Customer" body={(row) => row.user ? `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim() || row.user.email : "Guest / unavailable"} />
                    <Column field="amount" header="Amount" body={(row) => `${row.currency} ${Number(row.amount).toFixed(2)}`} />
                    <Column field="platform" header="Platform" body={(row) => titleCase(row.platform)} />
                    <Column field="source" header="Source" body={(row) => titleCase(row.source)} />
                    <Column field="provider" header="Gateway" body={(row) => titleCase(row.provider)} />
                    <Column field="providerTransactionId" header="Payment ID" />
                    <Column field="paidAt" header="Paid on" body={(row) => formatDate(row.paidAt)} />
                    <Column field="status" header="Status" body={(row) => titleCase(row.status)} />
                    <Column body={actions} header="Action" style={{ width: "80px" }} />
                </DataTable>
            </div>
        </div>
    );
}
